import { Box, useTheme } from '@mui/material';
import { PropsWithChildren } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  useResearchAssistantAvailability,
  useSyncIsWaitingForResponse,
} from '@verantos-dev/app-state-research-assistant';

import { GLOBAL_RA_WIDTH } from '../constants';
import { ResearchAssistantContainer } from '../containers';
import { useGlobalResearchAssistant } from '../hooks/useGlobalResearchAssistant';

const GlobalResearchAssistantShell = ({ children }: PropsWithChildren) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100%',
        borderRight: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        width: `${GLOBAL_RA_WIDTH}px`,
        transition: theme.transitions.create(['width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
      data-testid="global-research-assistant"
    >
      {children}
    </Box>
  );
};

const LegacyGlobalResearchAssistant = () => {
  const { isRAOpen, isRAEnabled, isOwner } = useGlobalResearchAssistant();

  if (!isRAOpen || !isRAEnabled) {
    return null;
  }

  return (
    <GlobalResearchAssistantShell>
      <ResearchAssistantContainer isOwner={!!isOwner} />
    </GlobalResearchAssistantShell>
  );
};

/**
 * Global Research Assistant component that can be mounted at the app level
 * Displays a sidebar that pushes content to the right when open.
 *
 * `research_assistant_gen_3` ON → null unconditionally. gen_3 RA is only
 * reachable inside a project (the project page mounts its own in-page
 * chat); there is no global/off-project RA surface (OR-6401).
 * `research_assistant_gen_3` OFF → {@link LegacyGlobalResearchAssistant},
 * a wholly separate RA system (the `/agent` namespace).
 */
export const GlobalResearchAssistant = () => {
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false);

  useResearchAssistantAvailability();
  // Mirrors researchAssistantSlice.isProcessing (Redux) into the Zustand
  // store's isWaitingForResponse field. The Redux slice still owns the
  // socket-driven processing state; the Zustand store is the consumer API
  // surface. TODO: remove once the full RA chat slice moves to Zustand.
  useSyncIsWaitingForResponse();

  if (isGen3Enabled) return null;

  return <LegacyGlobalResearchAssistant />;
};

export default GlobalResearchAssistant;
